import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { DEFAULT_EDITOR_TYPOGRAPHY, type EditorTypography } from "@caliu/types";
import { EDITOR_TYPOGRAPHY_RANGES } from "@caliu/utils";
import { cn } from "@/lib/utils";
import { scrollEdgeHairline, useContentScrolledUnder } from "../hooks/useContentScrolledUnder";
import { useNewsletterToggle } from "../hooks/useNewsletterToggle";
import { newNoteDateText } from "../lib/newNoteContent";
import {
  WHATS_NEW_HIGHLIGHTED_ICON,
  WHATS_NEW_TAG_ICONS,
  offersNewsletter,
  whatsNewAtLaunch,
  whatsNewPages,
  type WhatsNewPage,
  type WhatsNewSlide,
} from "../lib/whatsNew";
import { useAuthStore } from "../store/auth";
import { useUIStore } from "../store/ui";
import { Button } from "./ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { SettingsToggleRow } from "./settings/parts";
import { Icon } from "./ui/Icon";

/**
 * Mounted once at the app root (next to OnboardingGate). The decision was made
 * at launch (`whatsNewAtLaunch()` in main.tsx); this only waits for the app UI
 * to be up, and never presents over onboarding.
 */
export function WhatsNewGate() {
  const { pathname } = useLocation();
  const inApp = useAuthStore((s) => s.user !== null || s.localMode);
  const onboardingOpen = useUIStore((s) => s.onboardingOpen);
  const [show] = useState(whatsNewAtLaunch);
  const replays = useUIStore((s) => s.whatsNewReplays);
  // The replay count it was last dismissed at: a new replay opens it again.
  const [dismissedAt, setDismissedAt] = useState(-1);

  if (!show && replays === 0) return null;
  // Stays mounted once dismissed, so the close animation plays.
  const open = dismissedAt !== replays && inApp && pathname.startsWith("/app") && !onboardingOpen;
  return <WhatsNewDialog key={replays} open={open} onDismiss={() => setDismissedAt(replays)} />;
}

/**
 * Every highlight is its own illustrated page (a slide, ported from the Swift
 * sheet: `WhatsNewSheet.swift` + `WhatsNewHeroes.swift`): a hero card on the
 * note-list colour drawn from the real feature, then its title and text.
 */
function WhatsNewDialog({ open, onDismiss }: { open: boolean; onDismiss: () => void }) {
  const { t } = useTranslation();
  // Decided as it opens: turning the newsletter on from its page keeps the page.
  const [pages] = useState(() =>
    whatsNewPages(undefined, { newsletter: offersNewsletter(useAuthStore.getState().user) }),
  );
  const [index, setIndex] = useState(0);
  const page = pages[index];
  const isLast = index === pages.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDismiss()}>
      {/* The slide scrolls inside, between a fixed header and footer: the phone
          sheet itself is `touch-action: none` (vaul), so only an inner scroll
          region moves under a finger. */}
      {/* One height for every page, as Swift's page sheet (iPhone) and fixed
          780pt sheet (Mac): the pages swap without the sheet jumping. */}
      <DialogContent className="flex h-[calc(100dvh-max(12px,env(safe-area-inset-top)))] flex-col overflow-hidden md:h-[min(780px,calc(100dvh-2rem))]">
        {/* Root AGENTS.md "Fixed chrome over scrolling content": the header's
            hairline only once the slide scrolls under it (as in Settings),
            and a gradient rising out of the footer. */}
        <WhatsNewHeader />

        {/* Keyed per page: the next page starts at its top. */}
        <div key={page?.id} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {/* A tablet gets the phone sheet at full width: keep the slide at
              the iPad sheet's width (Swift's 460pt) instead of growing tiles. */}
          <div className="mx-auto w-full max-w-[460px]">
            {page && <WhatsNewPageContent page={page} />}
          </div>
        </div>

        <DialogFooter className="relative flex-col items-stretch gap-3 border-t-0 bg-canvas before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-6 before:bg-gradient-to-t before:from-canvas before:to-transparent">
          <div className="mx-auto flex w-full max-w-[460px] flex-col gap-3">
            {pages.length > 1 && (
              <div className="flex items-center justify-center gap-1.5" aria-hidden>
                {pages.map((p, i) => (
                  <span
                    key={p.id}
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      i === index ? "w-5 bg-accent" : "w-1.5 bg-border",
                    )}
                  />
                ))}
              </div>
            )}
            <Button size="lg" className="w-full" onClick={() => (isLast ? onDismiss() : setIndex(index + 1))}>
              {isLast ? t("onboarding.startWriting") : t("pro.continue")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Its own component so the hook attaches when the dialog's content mounts. */
function WhatsNewHeader() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const scrolled = useContentScrolledUnder(ref);
  return (
    <DialogHeader ref={ref} className={scrollEdgeHairline(scrolled)}>
      <DialogTitle>{t("whatsNew.title")}</DialogTitle>
    </DialogHeader>
  );
}

/** Each slide's hero and the i18n keys of its title and text. */
const SLIDES: Record<WhatsNewSlide, { hero: () => ReactNode; title: string; body: string }> = {
  tagIcons: { hero: () => <TagIconsHero />, title: "whatsNew.tagIconsTitle", body: "whatsNew.tagIconsBody" },
  newNoteStart: {
    hero: () => <NewNoteStartHero />,
    title: "whatsNew.newNoteStartTitle",
    body: "whatsNew.newNoteStartBody",
  },
  typography: { hero: () => <TypographyHero />, title: "whatsNew.typographyTitle", body: "whatsNew.typographyBody" },
};

function WhatsNewPageContent({ page }: { page: WhatsNewPage }) {
  const { t } = useTranslation();
  if (page.kind === "newsletter") return <NewsletterInvite />;
  const slide = SLIDES[page.id];
  return (
    <>
      <HeroCard>{slide.hero()}</HeroCard>
      <TextBlock title={t(slide.title)} body={t(slide.body)} />
    </>
  );
}

/** The hero surface: note-list colour, radius 28, the same padding all round. */
function HeroCard({ children }: { children: ReactNode }) {
  return (
    <div aria-hidden className="mx-4 mt-4 rounded-[28px] bg-notelist p-5">
      {children}
    </div>
  );
}

function TextBlock({ title, body, className }: { title: string; body: string; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 px-7 pt-7", className ?? "pb-6")}>
      <h2 className="font-headings text-[30px] font-semibold leading-[1.15] tracking-[-0.01em] text-ink">{title}</h2>
      <p className="text-base leading-normal text-ink-secondary">{body}</p>
    </div>
  );
}

/** A drawn bar of text: `fraction` of the width, 4px tall. */
function Line({ fraction, className }: { fraction: number; className?: string }) {
  return <span className={cn("block h-1 rounded-sm", className)} style={{ width: `${fraction * 100}%` }} />;
}

/** The caret, in the accent. */
function Caret({ className }: { className: string }) {
  return <span className={cn("block w-0.5 rounded-[1px] bg-accent", className)} />;
}

/**
 * The newsletter invite, last, for a signed-in account not on the list: a
 * drawn email, what it is, then the Settings toggle under the text (same hook,
 * same state), on the Settings surface. Off until the user turns it on.
 */
function NewsletterInvite() {
  const { t } = useTranslation();
  const { newsletter, online, failed, toggle } = useNewsletterToggle();
  return (
    <>
      <HeroCard>
        <NewsletterHero />
      </HeroCard>
      <TextBlock
        title={t("whatsNew.newsletterTitle")}
        body={failed ? t("settings.newsletterError") : t("whatsNew.newsletterBody")}
        className="pb-0"
      />
      <div className="mx-4 mb-6 mt-6">
        <SettingsToggleRow
          icon="mail"
          label={t("settings.newsletter")}
          checked={newsletter?.subscribed ?? false}
          disabled={!newsletter || !online}
          onChange={toggle}
          className="px-4 py-3"
        />
      </div>
    </>
  );
}

/* ── Heroes ───────────────────────────────────────────────────────────── */

/** 42 tag icons, A to Z, 6 × 7, telescope highlighted. */
function TagIconsHero() {
  return (
    <div className="grid grid-cols-6 gap-x-2 gap-y-[11px]">
      {WHATS_NEW_TAG_ICONS.map((name) => (
        <div
          key={name}
          className={cn(
            "flex aspect-square items-center justify-center rounded-xl border",
            name === WHATS_NEW_HIGHLIGHTED_ICON
              ? "border-[1.5px] border-accent bg-accent-bg text-accent"
              : "border-border-light bg-canvas text-ink",
          )}
        >
          <Icon name={name} size={22} />
        </div>
      ))}
    </div>
  );
}

/**
 * Three small new notes, one per kind of start (an empty heading, today's
 * date, an empty page), over the Settings row that picks it, its value in the
 * pop-up the web draws.
 */
function NewNoteStartHero() {
  const { t } = useTranslation();
  const today = newNoteDateText("date", new Date());
  const page = (label: string, selected: boolean, content: ReactNode) => (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
      <div
        className={cn(
          "h-[118px] w-full overflow-hidden rounded-[14px] bg-canvas p-2.5",
          selected ? "border-[1.5px] border-accent shadow-md" : "border border-border-light shadow-sm",
        )}
      >
        {content}
      </div>
      <span
        className={cn(
          "max-w-full truncate text-[12px]",
          selected ? "font-semibold text-accent" : "text-ink-secondary",
        )}
      >
        {label}
      </span>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2.5">
        {page(
          t("settings.newNoteStartH1"),
          false,
          <div className="flex items-center gap-[3px]">
            <span className="font-headings text-[17px] leading-none text-muted opacity-60">#</span>
            <Caret className="h-[18px]" />
          </div>,
        )}
        {page(
          t("settings.newNoteStartDate"),
          true,
          <div className="flex flex-col gap-1.5">
            <span className="line-clamp-2 font-headings text-[12px] leading-tight text-ink">
              {today}
            </span>
            <Caret className="h-[13px]" />
          </div>,
        )}
        {page(t("settings.newNoteStartEmpty"), false, <Caret className="h-[13px]" />)}
      </div>
      <div className="flex h-[46px] items-center gap-2.5 rounded-[14px] border border-border-light bg-canvas pl-3.5 pr-2">
        <Icon name="note-new" size={15} className="shrink-0 text-accent" />
        <span className="min-w-0 flex-1 truncate text-[14px] text-ink">{t("settings.newNoteStart")}</span>
        <span className="flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-active px-2.5 text-[13px] text-ink">
          {t("settings.newNoteStartDate")}
          <Icon name="chevron-up-down" size={11} className="opacity-60" />
        </span>
      </div>
    </div>
  );
}

/**
 * A note set in the prose font at the web's default sizing, over the
 * Typography sliders for size and paragraph spacing, with their labels and
 * values as Settings shows them (px).
 */
function TypographyHero() {
  const { t } = useTranslation();
  const typography = DEFAULT_EDITOR_TYPOGRAPHY;
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-1.5 rounded-[14px] border border-border-light bg-canvas p-4 shadow-md">
        <span className="font-headings text-[18px] leading-snug text-ink">{t("whatsNew.sampleMorningPages")}</span>
        <p className="font-prose text-[14px] text-ink-secondary" style={{ lineHeight: typography.lineHeight }}>
          {t("whatsNew.typographySample")}
        </p>
      </div>
      <div className="flex flex-col gap-3.5 px-1">
        <HeroSlider label={t("editorTypography.fontSize")} setting="size" value={typography.size} />
        <HeroSlider
          label={t("editorTypography.paragraphSpacing")}
          setting="paragraphSpacing"
          value={typography.paragraphSpacing}
        />
      </div>
    </div>
  );
}

/**
 * The Settings slider at rest: label and value, then the track filled up to
 * the knob, in the accent as the web's range input draws it (`accent-accent`).
 */
function HeroSlider({ label, setting, value }: { label: string; setting: keyof EditorTypography; value: number }) {
  const { min, max } = EDITOR_TYPOGRAPHY_RANGES[setting];
  const percent = `${((value - min) / (max - min)) * 100}%`;
  // The unit Settings shows (`TypographySettings`).
  const readout = `${value} px`;
  return (
    <div className="flex flex-col gap-2 text-[13px] text-ink">
      <span className="flex items-baseline justify-between">
        <span>{label}</span>
        <span className="tabular-nums">{readout}</span>
      </span>
      <span className="relative block h-[18px]">
        <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-border" />
        <span className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent" style={{ width: percent }} />
        <span
          className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-sm"
          style={{ left: percent }}
        />
      </span>
    </div>
  );
}

/** The newsletter as it lands: an email from Caliu with "What's new" as its subject. */
function NewsletterHero() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border-light bg-canvas p-4 shadow-md">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-on-accent">
          <Icon name="leaf" size={15} />
        </span>
        <span className="flex-1 text-[13px] font-semibold text-ink">Caliu</span>
        <Icon name="mail" size={14} className="text-muted" />
      </div>
      <span className="h-px bg-border-light" />
      <span className="font-headings text-[17px] text-ink">{t("whatsNew.title")}</span>
      <div className="flex flex-col gap-[7px]">
        {[1, 0.92, 0.6].map((fraction, i) => (
          <Line key={i} fraction={fraction} className="bg-ink-secondary opacity-40" />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-[54px] shrink-0 items-center justify-center rounded-lg bg-accent-bg text-accent">
          <Icon name="spark" size={15} />
        </span>
        <div className="flex flex-1 flex-col gap-1.5">
          <Line fraction={0.9} className="bg-ink-secondary opacity-40" />
          <Line fraction={0.55} className="bg-ink-secondary opacity-40" />
        </div>
      </div>
    </div>
  );
}
